import React, { useState, useEffect, useRef } from "react";
import { IconButton } from "@mui/material";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SideBarList from "./SideBarList";
import { mainAppGridStyle } from "../../themes/mobiliTheme";
import useStore from "../../helpers/useStore";
import { UserContext } from "../../stores/User/User.provider";
import { observer } from "mobx-react";
import useChiefDebugAccess from "../../hooks/useChiefDebugAccess";

const drawerWidth = 180;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(5)} + 1px)`,
  [theme.breakpoints.down("sm")]: {
    width: 0,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",

    ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": { background: mainAppGridStyle.background, ...openedMixin(theme) },
    }),
    ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": {
        border: "unset",
        background: mainAppGridStyle.background,
        ...closedMixin(theme),
      },
    }),
  })
);

export const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 0),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface SideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const SideDrawer = observer(({ isOpen, onClose }: SideDrawerProps) => {
  const { user } = useStore(UserContext);
  const UserStore = useStore(UserContext);
  const hasChiefDebugAccess = useChiefDebugAccess();
  const theme = useTheme();
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (isOpen && drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  if (!user) {
    return null;
  }

  return (
    <Drawer variant="permanent" open={isOpen} ref={drawerRef}>
      <DrawerHeader>
        <IconButton onClick={onClose}>
          {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </DrawerHeader>
      <SideBarList
        isOpen={isOpen}
        menus={[
          { id: 1, name: "Dashboard", linkTo: "/dashboard" },
          { id: 2, name: "Patient", linkTo: "/patient" },
          { id: 3, name: "Termin Plan", linkTo: "/termin" },
          ...(hasChiefDebugAccess ? [{ id: 15, name: "MobiliCal", linkTo: "/mobilical" }] : []),
          {
            id: 13,
            name: "Verkauf",
            linkTo: "/sales",
            // isDisabled: !UserStore.hasPermission("dashboard"),
          },
          { id: 12, name: "Register", linkTo: "/registry" },
          {
            id: 14,
            name: "e-Akte",
            linkTo: "/e-akte",
            isDisabled: !UserStore.hasPermission("featurePreview"),
          },
          {
            id: 4,
            name: "Kasse",
            linkTo: "/transactions",
            isDisabled: !UserStore.hasPermission("ledger"),
          },
          {
            id: 9,
            name: "Administration",
            linkTo: "/admin",
            isDisabled: !UserStore.hasPermission("adminPage"),
          },
          {
            id: 10,
            name: "History",
            linkTo: "/history",
            //isDisabled: !UserStore.hasPermission("adminPage"),
          },
          {
            id: 11,
            name: "Controlling",
            linkTo: "/biDashboard",
            isDisabled: !UserStore.hasPermission("dashboard"),
          },
        ]}
        onClick={onClose}
      />
    </Drawer>
  );
});

export default SideDrawer;
